import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, GraduationCap, ShieldCheck } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SchoolDetailPage() {
  const { schoolId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/schools/${schoolId}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [schoolId]);

  if (notFound) return <div className="py-24 text-center text-slate-500">School not found. <Link to="/schools" className="text-[#0055FF] underline">Back to Schools</Link></div>;
  if (!data) return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-4 border-[#0055FF] border-t-transparent" /></div>;

  const { school, country, sports } = data;
  const facts = [
    ["Type", school.type], ["Location", `${school.city}, ${school.country}`],
    ["Association", school.association], ["Notable", school.notable],
  ];

  return (
    <div>
      <PageHero topic={`${school.name} campus athletics`} title={school.name} subtitle={`${school.type} in ${school.city}, ${school.country} — ${school.notable}.`} listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-10 px-4 py-12 sm:px-6">
        <Link to="/schools" data-testid="back-to-schools" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0055FF] hover:underline"><ArrowLeft className="h-4 w-4" /> <T>All Schools & Colleges</T></Link>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="school-facts">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0055FF] text-white"><GraduationCap className="h-5 w-5" /></span>
              <h2 className="text-lg font-bold text-slate-900"><T>Institution Facts</T></h2>
            </div>
            <dl className="mt-4 space-y-3">
              {facts.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-2 text-sm">
                  <dt className="shrink-0 font-bold text-slate-500"><T>{k}</T></dt>
                  <dd className="text-right text-slate-800"><T>{String(v)}</T></dd>
                </div>
              ))}
            </dl>
            {country && (
              <p className="mt-4 text-xs text-slate-500">
                <span className="font-bold"><T>Country</T>:</span> {country.flag} {country.name} · <T>Olympic committee</T>: {country.olympic_committee}
              </p>
            )}
            <p className="mt-4 flex items-start gap-1.5 rounded-xl bg-amber-50 p-3 text-[11px] text-amber-800">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <T>Youth safeguarding: SportsPulse never publishes private information about minors. Schedules and results come only from official institutional sources.</T>
            </p>
          </div>
          <div className="lg:col-span-2">
            <AIContent topic={`Athletics heritage of ${school.name}`} context={`Editorial about ${school.name}, a ${school.type} in ${school.city}, ${school.country}, known for: ${school.notable}. Sports offered: ${school.sports_offered.join(", ")}. Cover its sporting culture, pathways and community role. Never rank or identify current minors.`} />
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="school-sports">
          <h2 className="mb-4 text-xl font-bold text-slate-900"><T>Sports Program</T></h2>
          <div className="flex flex-wrap gap-2">
            {school.sports_offered.map((sp) => <span key={sp} className="rounded-full bg-blue-50 px-3 py-1.5 text-sm font-bold text-[#0055FF]">{sp}</span>)}
          </div>
          {sports.length > 0 && (
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {sports.map((s) => (
                <div key={s.id} className="flex items-start gap-3 rounded-xl border border-slate-200 p-4">
                  <span className="text-2xl">{s.icon}</span>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{s.name}</p>
                    <p className="text-xs text-slate-500"><T>Governing body</T>: <a href={s.governing_body.url} target="_blank" rel="noreferrer" className="text-[#0055FF] hover:underline">{s.governing_body.name}</a></p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
